import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Smartphone, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AuthenticatorQr, OtpBoxes } from "@/components/TwoFactorStep";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { authHeader, errText } from "@/lib/aadhaar";

export const TotpSettings = ({ role }) => {
  const { L } = useLang();
  const [st, setSt] = useState(null);
  const [setup, setSetup] = useState(null);
  const [disabling, setDisabling] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const auth = authHeader(role);

  const load = () => api.get("/auth/2fa/status", auth).then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); } catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const start = () => run(async () => { setSetup((await api.post("/auth/2fa/totp/setup", {}, auth)).data); setCode(""); });
  const confirm = () => run(async () => { await api.post("/auth/2fa/totp/enable", { code }, auth); setSetup(null); setCode(""); await load(); }, L("Authenticator app is on", "Authenticator ऐप चालू हो गया"));
  const disable = () => run(async () => { await api.post("/auth/2fa/totp/disable", { code }, auth); setDisabling(false); setCode(""); await load(); }, L("Switched to email codes", "अब ईमेल कोड से सत्यापन होगा"));

  if (!st) return null;
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6" data-testid="totp-settings">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#F0F4F8] grid place-items-center shrink-0"><ShieldCheck className="w-5 h-5 text-emerald-600" /></div>
          <div>
            <div className="font-extrabold text-[#0A192F]">{L("Two-step verification", "दो-चरण सत्यापन")}</div>
            <p className="text-sm text-slate-500" data-testid="totp-status-text">
              {st.totp_enabled ? L("On · Authenticator app (email code as backup)", "चालू · Authenticator ऐप (बैकअप: ईमेल कोड)")
                : L(`On · Email code to ${st.email_hint}`, `चालू · ${st.email_hint} पर ईमेल कोड`)}
            </p>
          </div>
        </div>
        {!setup && !disabling && (st.totp_enabled
          ? <Button variant="outline" onClick={() => { setDisabling(true); setCode(""); }} className="bg-transparent rounded-full" data-testid="totp-disable-btn">{L("Turn off authenticator", "Authenticator बंद करें")}</Button>
          : <Button onClick={start} disabled={busy} className="rs-cta rounded-full" data-testid="totp-setup-btn"><Smartphone className="w-4 h-4 mr-1" />{L("Use authenticator app", "Authenticator ऐप लगाएँ")}</Button>)}
      </div>
      {(setup || disabling) && (
        <div className="mt-5 space-y-4">
          {setup && <AuthenticatorQr url={setup.otpauth_url} secret={setup.secret} />}
          {disabling && <p className="text-sm text-slate-600">{L("Enter the current code from your authenticator app to turn it off.", "बंद करने के लिए authenticator ऐप का मौजूदा कोड डालें।")}</p>}
          <OtpBoxes value={code} onChange={setCode} testid="totp-settings-code" />
          <div className="flex gap-2">
            <Button onClick={setup ? confirm : disable} disabled={busy || code.length !== 6} className="rs-cta rounded-full" data-testid="totp-confirm-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : setup ? L("Turn on", "चालू करें") : L("Turn off", "बंद करें")}
            </Button>
            <Button variant="outline" onClick={() => { setSetup(null); setDisabling(false); }} className="bg-transparent rounded-full" data-testid="totp-cancel-btn">{L("Cancel", "रद्द करें")}</Button>
          </div>
        </div>
      )}
    </div>
  );
};
